import algosdk, { Transaction } from 'algosdk';
import {
  isAddress,
  Account,
  Address,
  SimulateContractParameters,
  WriteContractParameters,
  erc20Abi,
} from 'viem';
import { tokenContractAbi, tokenManagerContractAbi } from '../_utils/tokenManager';
import { AssetType } from '../_utils';
import { GASLIMIT } from '../constants/constants';
import { BLOCKCHAINS, getEvmChainId, getLoginChainId, getTmcByEvmChainId, getTmcByWhChain, getWhChainId } from '../wallets/providers';
import { normalizeAddress } from '@libs/shared/helpers/codex.helper';
import { PeraConnector } from '../wallets/algorand/pera.service';
import { getCorrectWalletClient, HTTPSRPCs, madePRCRequest } from '../wallets/rpc.service';
import { errorHandler, isCancelError } from '../_utils/errorHandlers';

const sendTransaction = async (tnxArray: Transaction[], client, withSignedTxns = false) => {
  try {
    // ========== Group Transactions =====================
    if (tnxArray.length > 1) {
      const groupID = algosdk.computeGroupID(tnxArray);
      for (let i = 0; i < tnxArray.length; i++) {
        tnxArray[i].group = groupID;
      }
    }
    //TODO provide this fn without losing context
    const signedTxns = await PeraConnector.peraWallet.signTransaction([tnxArray.map((txn) => ({txn}))]);

    const tnx = await client.sendRawTransaction(signedTxns).do();

    if (withSignedTxns) {
      return { tnx, signedTxns };
    } else {
      return tnx;
    }
  } catch (error) {
    throw error;
  }
};

export const getAllowedBalance = async (token: string, wallet: string, chain: BLOCKCHAINS): Promise<bigint> => {
  const tmcContract = getTmcByWhChain(chain);

  const allowedBalanceFunc = async (publicClient) => {
    const data = (await publicClient.readContract({
      address: token as `0x${string}`,
      abi: tokenContractAbi,
      functionName: 'allowance',
      args: [wallet, tmcContract],
    })) as bigint;
    return data;
  }

  return await madePRCRequest(getEvmChainId(chain), allowedBalanceFunc);
};

export const approveBalance = async ({ sender, amountFixed, assetIndex, senderChain }: IApproveProps) => {
    const evmSenderChainId = getEvmChainId(senderChain);
    const tmcContract = getTmcByEvmChainId(evmSenderChainId);
    const params: SimulateContractParameters = {
      address: assetIndex,
      abi: tokenContractAbi,
      functionName: 'approve',
      args: [tmcContract, amountFixed],
      gas: GASLIMIT * 10,
      account: sender,
    };

    const writeContractFunc = async (publicClient, rpc, rpsIndex) => {
      const { request } = await publicClient.simulateContract(params);
      const walletClient = await getCorrectWalletClient(evmSenderChainId, rpsIndex);

      if (walletClient) {
        const [address] = await walletClient.getAddresses();
        const PKchainId = await publicClient.getChainId();
        const writeParams: WriteContractParameters = {
          address: request.address,
          chain: request.chain,
          abi: request.abi,
          functionName: request.functionName,
          args: request.args,
          account: sender,
        };
        try {
          const hash = await walletClient.writeContract(writeParams);
          return { hash, publicClient };
        } catch (err) {
          const whChainId = getWhChainId(senderChain);
          if (!isCancelError(err?.message) && (HTTPSRPCs[whChainId].length - 1) > rpsIndex) {
            errorHandler(err, { customMsg: "error_msg.rpc_error" })
          };
          throw err;
        }
      }
    }

    return await madePRCRequest(evmSenderChainId, writeContractFunc);
};

export const depositBalance = async ({
  amountFixed,
  assetIndex,
  sender,
  logInAcc,
  loginChain,
  senderChain,
}: IDepositProps) => {
  const evmSenderChainId = getEvmChainId(senderChain);
  const tmcContract = getTmcByEvmChainId(evmSenderChainId);
  const loginChainId = getLoginChainId(loginChain);
  const normalizedAddress = normalizeAddress(logInAcc, loginChainId, true);

  const simulateContractData = {
    account: sender,
    address: tmcContract as `0x${string}`,
    abi: tokenManagerContractAbi,
    gas: GASLIMIT * 10,
  } as unknown as SimulateContractParameters;

  if (assetIndex) {
    simulateContractData.functionName = 'depositToCodex';
    simulateContractData.args = [assetIndex, amountFixed, normalizedAddress, loginChainId];
  } else {
    simulateContractData.functionName = 'depositGasTokenToCodex';
    simulateContractData.args = [normalizedAddress, loginChainId];
    simulateContractData.value = amountFixed;
  }

  const writeContractFunc = async (publicClient, rpc, rpsIndex) => {
    const { request } = await publicClient.simulateContract(simulateContractData);
    const  walletClient = await getCorrectWalletClient(evmSenderChainId, rpsIndex);

    if (walletClient) {
      try {
        const hash = await walletClient.writeContract({
          address: request.address,
          chain: request.chain,
          abi: request.abi,
          functionName: request.functionName,
          args: request.args,
          value: request.value,
          account: sender,
        });
        return { hash, publicClient };
      } catch (err) {
        const whChainId = getWhChainId(senderChain);
        if (!isCancelError(err?.message) && (HTTPSRPCs[whChainId].length - 1) > rpsIndex) {
          errorHandler(err, { customMsg: "error_msg.rpc_error" })
        };
        throw err;
      }
    }
  }

  return await madePRCRequest(evmSenderChainId, writeContractFunc);
};

const tokenCache: Record<string, { decimals?: number; name?: string; symbol?: string }> = {};

const getEVMBalance = async (token: `0x${string}`, wallet: string, chainId: number): Promise<string> => {
  const getBalanceFunc = async (publicClient) => {
    const balance = await publicClient.readContract({
      address: token,
      abi: tokenContractAbi,
      functionName: 'balanceOf',
      args: [wallet],
    });
    return balance.toString() ?? "0";
  }

  return await madePRCRequest(chainId, getBalanceFunc);
};

const getEvmAssets = async (tokens: `0x${string}`[], wallet: string, chainId: number): Promise<AssetType[]> => {
  const assets: AssetType[] = [];
  if (!wallet || !isAddress(wallet)) return assets;

  const getAssetsFunc = async (publicClient) => {
    return await Promise.allSettled(
      tokens.map(async (token) => {
        const tokenKey = `${token}_${chainId}`;
        if (!tokenCache[tokenKey]) {
          tokenCache[tokenKey] = {};
        }

        const tokenData = tokenCache[tokenKey];
        if (!tokenData.decimals) {
          tokenData.decimals = await publicClient.readContract({
            address: token,
            abi: erc20Abi,
            functionName: 'decimals',
          });
        }
        if (!tokenData.name) {
          tokenData.name = await publicClient.readContract({
            address: token,
            abi: erc20Abi,
            functionName: 'name',
          });
        }
        if (!tokenData.symbol) {
          tokenData.symbol = await publicClient.readContract({
            address: token,
            abi: erc20Abi,
            functionName: 'symbol',
          });
        }

        return [0n, tokenData.decimals, tokenData.name, tokenData.symbol];
      })
    );
  }

  const tokenInfo = await madePRCRequest(chainId, getAssetsFunc);

  tokenInfo.forEach((result, index) => {
    if (result.status === "fulfilled") {
      const [balance, decimal, name, symbol] = result.value;

      const asset: AssetType = {
        index: tokens[index],
        amount: balance.toString(),
        decimal,
        name,
        unit_name: symbol,
        img: '',
        usd_value: 0,
      };
  
      assets.push(asset);
    } else {
      console.log("Error on getEvmAssets: ", result.reason);
    } 
  });

  return assets;
};

async function waitForTxOrReplacement(
  publicClient: any,
  hash: `0x${string}`,
  maxRetries = 30,
  pollingInterval = 3000
): Promise<{ replaced: boolean; receipt?: any }> {
  let replaced = false;

  for (let i = 0; i < maxRetries; i++) {
    const receipt = await publicClient.getTransactionReceipt({ hash }).catch(() => null);
    if (receipt) {
      // Normal success: transaction was confirmed
      return { replaced, receipt };
    }

    // If there's no receipt, check if the raw transaction is in mempool
    const tx = await publicClient.getTransaction({ hash }).catch(() => null);
    if (!tx) {
      // Means the node no longer sees this transaction => likely replaced or dropped
      replaced = true;
      return { replaced };
    }

    // Not confirmed yet, wait a little and try again
    await new Promise((resolve) => setTimeout(resolve, pollingInterval));
  }

  // Still no receipt after many retries => likely stuck or replaced
  return { replaced: true };
}

export const waitForConfirmation = async(hash, publicClient) => {

  const defaultWaitPromise = publicClient.waitForTransactionReceipt({ hash });

  const TIMEOUT_MS = 60_000; // 1 minute, for example
  let timerId: NodeJS.Timeout;
  const timeoutPromise = new Promise((_, reject) => {
    timerId = setTimeout(() => {
      reject(new Error("Transaction receipt timed out, might be replaced."));
    }, TIMEOUT_MS);
  });

  let receipt;
  try {
    // Race between the default wait and a time limit
    receipt = await Promise.race([defaultWaitPromise, timeoutPromise]);
    clearTimeout(timerId);
  } catch (err) {
    // Means the default approach timed out
    console.log("Default waitForTransactionReceipt timed out, falling back...");
  }

  // If we *still* have no receipt, do the fallback approach:
  if (!receipt) {
    const { replaced, receipt: fallbackReceipt } = await waitForTxOrReplacement(publicClient, hash);
    if (replaced && !fallbackReceipt) {
      // The transaction was definitely replaced or dropped.
      return { status: "replaced" };
    }
    receipt = fallbackReceipt;
  }
}

interface IApproveProps {
  amountFixed: bigint, 
  assetIndex: `0x${string}`, 
  sender: Account | Address;
  senderChain: BLOCKCHAINS
}

interface IDepositProps extends IApproveProps {
  logInAcc: string, 
  loginChain: BLOCKCHAINS
}

export const ALGOContractServices = {
  sendTransaction,
};

export const EVMContractServices = {
  getAllowedBalance,
  approveBalance,
  depositBalance,
  getEvmAssets,
  getEVMBalance,
  waitForConfirmation
};
